<!--
  THE FIRST-RUN INTRO — "Filmstrip".

  Jovii is a shared film night, so the catalogue rushes past and one thing is
  chosen: poster tiles fly at the camera in perspective, the flow arrests, and
  the J that is left TURNS INTO the wordmark — it slides into its slot while
  O V I I arrive around it. The mark is never separate from its own name.

  WHY THIS IS A LIVE COMPONENT AND NOT AN MP4. The brief is that it must survive
  a rotation MID-ANIMATION. A video cannot: it is baked at one aspect ratio and
  a phone turned sideways at 1.5s either letterboxes or crops the mark out of
  frame. Everything here is laid out from the live viewport, so a rotation
  re-flows the composition on the next frame and the animation keeps its place
  on the clock. The tiles, the lockup size and the tunnel spread are all
  functions of the CURRENT width and height.

  PORTRAIT AND LANDSCAPE ARE DIFFERENT COMPOSITIONS, not one scaled. Portrait
  has height to spend and no width, so the tunnel is narrow and tall and the
  lockup is large; landscape is the reverse. `layout()` below is the whole
  difference and it is re-read every frame.

  IT RUNS ONCE PER COLD START, not once ever. `jt.intro.v1` in sessionStorage:
  the session storage area is destroyed with the top-level browsing context, so
  the flag dies with the tab, with a Home Screen web app's process and with the
  native shell's process — and survives a reload and any in-session navigation.
  That is Netflix's rule: the ident on every open where the app was not already
  loaded, never twice while it is running. `shouldPlayIntro()` owns the decision
  so the caller never mounts this at all on a warm start. `intro.ts` has the
  reasoning, including why an iOS process that refuses to die (OPEN-DEFECTS B2)
  would make this look broken again.

  ACCESSIBILITY. `prefers-reduced-motion` collapses the whole thing to a still
  lockup and a short fade — the house rule everywhere else in this app. The
  overlay is aria-hidden and inert: it is decoration over a booting app, and a
  screen reader should be reading the app, not this. Any key, pointer or touch
  skips it, because a five second gate with no exit is a bug.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { INTRO_KEY, markIntroSeen } from './intro';
  import { J, LETTERS, WORD_ASPECT, glyphSrc } from './wordmark';

  const { done }: { done: () => void } = $props();

  /* The palette matrix accents, in matrix order — the poster tiles are the
     brand's own colours, so the catalogue rushing past is literally Jovii's. */
  const PALETTE = [
    '#EF2034', '#FF653C', '#F0B64D', '#B4D464', '#32BA8E',
    '#40BED8', '#4266FF', '#A885E8', '#ED82B7'
  ];

  /* Letter geometry comes from `wordmark.ts`, which bundles wordmark.json at
     build time — no fetch, so the intro still draws on its first frame, and no
     private copy of the table, so it cannot disagree with Logo or the splash. */

  /* Deterministic tile field: a seeded hash, never the platform RNG, so the
     intro is identical on every device and every reload. A brand asset that
     differs run to run cannot be reviewed or regression-tested. */
  const rand = (seed: string): number => {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return ((h >>> 0) % 100000) / 100000;
  };
  const TILES = Array.from({ length: 16 }, (_, i) => ({
    i,
    hue: PALETTE[Math.floor(rand(`t${i}h`) * PALETTE.length)]!,
    x: (rand(`t${i}x`) - 0.5) * 2.1,
    y: (rand(`t${i}y`) - 0.5) * 1.5,
    delay: rand(`t${i}d`) * 1.8,
    dur: 1.15 + rand(`t${i}u`) * 0.75,
    spin: (rand(`t${i}r`) - 0.5) * 16
  }));

  const DURATION = 4.9;

  let t = $state(0);              // seconds on the intro clock
  let vw = $state(1280);
  let vh = $state(720);
  let leaving = $state(false);
  let reduced = $state(false);

  /* ── the motion vocabulary, shared with the Remotion source ─────────────── */
  const clamp = (v: number, a = 0, b = 1): number => Math.min(b, Math.max(a, v));
  /* Fast out, long tail: the house curve for anything entering. */
  const outQuint = (p: number): number => 1 - Math.pow(1 - clamp(p), 5);
  const inOut = (p: number): number => {
    const c = clamp(p);
    return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2;
  };
  const ramp = (from: number, to: number, ease = inOut): number =>
    ease(clamp((t - from) / (to - from)));
  /* Crosses its target once and comes back. Nothing arrives at its final size:
     a mark that eases to exactly 1 and stops reads as a slideshow. */
  const settle = (delay: number, period = 0.62, damp = 5.2): number => {
    const p = t - delay;
    if (p <= 0) return 0;
    return 1 - Math.exp(-damp * p) * Math.cos((2 * Math.PI * p) / period);
  };

  /*
    PORTRAIT vs LANDSCAPE. Read every frame from the live viewport, so a
    rotation part-way through simply produces different numbers on the next
    frame and the animation continues from where it is on the clock.
  */
  const layout = $derived.by(() => {
    const portrait = vh > vw;
    const min = Math.min(vw, vh);
    return {
      portrait,
      /* The word is sized off the NARROW edge so it can never overflow the
         short axis, then capped against width so a long word cannot run off a
         landscape phone either. */
      wordH: Math.min(min * (portrait ? 0.17 : 0.19), (vw * 0.82) / WORD_ASPECT),
      heroH: min * (portrait ? 0.3 : 0.34),
      tileH: min * (portrait ? 0.26 : 0.22),
      /* Portrait spreads the tunnel vertically, landscape horizontally: the
         axis with room to travel is the one the posters use. */
      spreadX: vw * (portrait ? 0.3 : 0.36),
      spreadY: vh * (portrait ? 0.36 : 0.3),
      perspective: min * (portrait ? 0.95 : 0.85)
    };
  });

  const become = $derived(ramp(2.95, 3.9));
  const arrive = $derived(settle(2.2));
  const fadeIn = $derived(ramp(2.15, 2.45, outQuint));

  /* The lockup, in pixels, for the CURRENT viewport and morph position. */
  const rig = $derived.by(() => {
    const L = layout;
    const wordW = L.wordH * WORD_ASPECT;
    const jWord = { w: wordW * J.w, h: L.wordH * J.h };
    const jHero = { h: L.heroH, w: L.heroH * (J.w / J.h) * WORD_ASPECT };
    const scale = reduced ? 1 : 0.66 + arrive * 0.34;
    const jW = (jHero.w + (jWord.w - jHero.w) * become) * scale;
    const jH = (jHero.h + (jWord.h - jHero.h) * become) * scale;
    const unitW = jW / J.w;
    const unitH = jH / J.h;
    /* At t=0 the J is centred; at t=1 the WORD is centred, which puts the J
       left of centre. That slide is the gesture: the logo makes room for its
       own name. `unitW` is the whole word's width (J.w is a fraction of it), so
       -unitW/2 centres whatever the table says the word is: JOVII's second I
       made the word wider and the J's slide longer, and nothing here changed. */
    const originX = (-jW / 2 - J.x * unitW) * (1 - become) + (-unitW / 2) * become;
    return { unitW, unitH, originX, originY: -unitH / 2 };
  });

  /* ── the clock ──────────────────────────────────────────────────────────── */
  let raf = 0;
  let finished = false;

  const finish = (): void => {
    if (finished) return;
    finished = true;
    markIntroSeen();
    leaving = true;
    /* Let the wash fade before unmounting, so the app does not pop in. */
    window.setTimeout(done, 320);
  };

  onMount(() => {
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const size = (): void => {
      vw = window.innerWidth;
      vh = window.innerHeight;
    };
    size();
    window.addEventListener('resize', size, { passive: true });
    /* iOS fires orientationchange BEFORE innerWidth/innerHeight settle, so the
       resize listener above is the one that matters; this is a belt-and-braces
       re-read a frame later for the browsers that do not fire resize on
       rotation at all. */
    const rotated = (): void => {
      window.requestAnimationFrame(size);
    };
    window.addEventListener('orientationchange', rotated, { passive: true });

    if (reduced) {
      /* One still lockup, briefly, then out. */
      t = DURATION;
      const timer = window.setTimeout(finish, 900);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener('resize', size);
        window.removeEventListener('orientationchange', rotated);
      };
    }

    const start = performance.now();
    const tick = (now: number): void => {
      t = (now - start) / 1000;
      if (t >= DURATION) {
        finish();
        return;
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);

    /* Any input skips. A five second gate with no exit is a bug. */
    const skip = (): void => finish();
    window.addEventListener('keydown', skip);
    window.addEventListener('pointerdown', skip);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
      window.removeEventListener('orientationchange', rotated);
      window.removeEventListener('keydown', skip);
      window.removeEventListener('pointerdown', skip);
    };
  });

  const asset = (glyph: string): string => glyphSrc(glyph, INTRO_KEY);
</script>

<div
  class="intro"
  class:leaving
  aria-hidden="true"
  inert
  style:--wine="#140409"
>
  {#if !reduced}
    <div class="tunnel" style:perspective="{layout.perspective}px">
      {#each TILES as tile (tile.i)}
        {@const p = inOut(clamp((t - tile.delay) / tile.dur))}
        {@const speed = p * p}
        <div
          class="poster"
          style:width="{layout.tileH * (2 / 3)}px"
          style:height="{layout.tileH}px"
          style:margin-left="{(-layout.tileH * (2 / 3)) / 2}px"
          style:margin-top="{-layout.tileH / 2}px"
          style:background={tile.hue}
          style:translate="{tile.x * layout.spreadX * p}px {tile.y * layout.spreadY * p}px {-1500 + p * 2120}px"
          style:rotate="{tile.spin * p}deg"
          style:scale={0.22 + p * 2.28}
          style:filter="blur({speed * 13}px)"
          style:opacity={p <= 0 ? 0 : p < 0.12 ? p / 0.12 : p > 0.72 ? Math.max(0, (1 - p) / 0.28) * 0.9 : 0.9 + (1 - p) * 0.1}
        ></div>
      {/each}
    </div>
  {/if}

  <div class="stage" style:opacity={reduced ? 1 : fadeIn}>
    <div class="lockup">
      {#each LETTERS as L, i (L.n)}
        <!-- O V I I arrive in reading order, stagger index 1..4. The step is
             0.11 of the morph rather than the 0.13 four letters had: with a
             fifth letter the last one has to start by 0.62 to be fully in by
             the time `become` reaches 1, or the word settles without its I. -->
        {@const stagger =
          L.n === 'J'
            ? 1
            : outQuint(clamp((become - (0.18 + i * 0.11)) / 0.34))}
        <img
          class="glyph"
          src={asset(L.glyph)}
          alt=""
          decoding="async"
          fetchpriority="high"
          style:left="{rig.originX + L.x * rig.unitW}px"
          style:top="{rig.originY + L.y * rig.unitH}px"
          style:width="{L.w * rig.unitW}px"
          style:height="{L.h * rig.unitH}px"
          style:opacity={reduced ? 1 : stagger}
          style:translate="{L.n === 'J' ? 0 : (1 - stagger) * rig.unitW * 0.06}px 0px"
        />
      {/each}
    </div>
  </div>
</div>

<style>
  .intro {
    position: fixed;
    inset: 0;
    z-index: 200; /* above --z-sheet (60): nothing outranks the boot screen */
    background: var(--wine);
    overflow: hidden;
    /* The app is booting underneath; never let a stray tap reach it. */
    touch-action: none;
    transition: opacity 300ms cubic-bezier(0.7, 0, 0.84, 0);
  }
  .intro.leaving {
    opacity: 0;
  }

  .tunnel,
  .stage {
    position: absolute;
    inset: 0;
  }
  .tunnel {
    transform-style: preserve-3d;
  }

  .poster {
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 14px;
    will-change: transform, opacity, filter;
  }

  .stage {
    display: grid;
    place-items: center;
  }
  /* A zero-size anchor at the centre of the frame; the letters are absolutely
     positioned around it from measured geometry, which is what keeps the
     lockup exact at any viewport. */
  .lockup {
    position: relative;
    width: 0;
    height: 0;
  }
  .glyph {
    position: absolute;
    display: block;
    will-change: transform, opacity;
    /*
      THE GLOBAL IMAGE RESET WOULD COLLAPSE THESE TO NOTHING.

      `app.css` sets `img { max-width: 100% }` — correct everywhere else, and
      fatal here: a percentage max-width resolves against the CONTAINING BLOCK,
      and `.lockup` is deliberately a zero-size anchor so the letters can be
      positioned around its centre from measured geometry. 100% of zero is
      zero, so every glyph computed to `width: 0px` while keeping its height,
      and the mark was invisible with no error anywhere.

      Overriding the cap is the fix; the sizes here are already computed from
      the viewport, so nothing needs clamping.
    */
    max-width: none;
  }
</style>
